/**
 * Guide engine v3 - session controller (#1029, epic #1020). Spec R7 / R8 / R9 / R11.
 *
 * The host layer that composes the v3 pieces into the running app (the "wired
 * at cutover" seam the lower layers left open):
 *   - catalog: fetched once the session is ready; a local completion patches the
 *     in-memory list so launcher badges update without a refetch (R7);
 *   - launch: auto-start of `required` guides for new users (launchPolicy) plus
 *     the manual `start(moduleId)` the launcher calls (#1010);
 *   - load: fetches + validates the locale package (loader), maps a failure to
 *     the error chip - Exit is the only out (R11);
 *   - sandbox: swaps into the hidden practice-space workspace for
 *     `sandbox: true` guides and restores the prior workspace on exit or
 *     completion (R8);
 *   - progress: writes `completed` on `_exit` only; an abandon writes nothing
 *     (R7);
 *   - audio + i18n: narration per step show with replay, voice persisted via
 *     voicePrefs, guide locale persisted in the user profile, engine chrome
 *     strings per locale (R9).
 *
 * Renders the GuideRuntimeProvider + GuideOverlay around the app so the whole
 * tree can host a guide; the runtime is keyed per run so every start is a
 * fresh forward-only walk (no resume, R7).
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useUserSession } from '../contexts/UserSessionContext.jsx';
import { useGroup } from '../contexts/GroupContext.jsx';
import { DEFAULT_LOCALE } from './contract';
import { loadGuidePackage } from './loader';
import { ensureCdnCookies } from './cdnAuth';
import { fetchCatalog, writeCompleted, computeEffectivePriority } from './catalog/catalogClient';
import { pickAutoStart } from './launch/launchPolicy';
import { createAudioController } from './audio/audioController';
import { getVoice, setVoice } from './audio/voicePrefs';
import { fetchLanguageConfig, availableGenders } from './i18n/languageConfig';
import { getEngineStrings } from './i18n/engineStrings';
import { GuideRuntimeProvider, GuideRuntimeContext } from './runtime/GuideRuntimeContext.jsx';
import GuideOverlay from './overlay/GuideOverlay.jsx';
import GuideErrorChip from './overlay/GuideErrorChip.jsx';
import {
  DEFAULT_GUIDE_STRINGS, LOAD_ERROR_REASON, SANDBOX_ERROR_REASON,
} from './overlay/strings';

export const GuidesContext = createContext(null);

// Short orient delay before a new-user auto-start so the app paints first.
const AUTO_START_DELAY_MS = 2000;
// A guide LanguageCode (en-US, es-MX). Anything else in the stored preference
// falls back to the default locale.
const LOCALE_CODE = /^[a-z]{2}-[A-Z]{2}$/;

/** Host phases. `running` means the runtime owns the show; the rest are ours. */
const PHASE = {
  IDLE: 'idle',
  LOADING: 'loading',
  PREPARING: 'preparing',
  RUNNING: 'running',
  ERROR: 'error',
};

/**
 * Plays narration when a step shows (R9) and parks the shown step for the
 * replay control. Lives inside the runtime provider; renders nothing. Consumes
 * the runtime context object directly (not the throwing hook) so it is also
 * renderable while no guide is active.
 */
const GuideAudioBridge = ({ controller, stepRef, getAudioOpts }) => {
  const rt = useContext(GuideRuntimeContext);
  useEffect(() => {
    if (!rt || !rt.isShown || !rt.step) return;
    stepRef.current = rt.step;
    // Keep CDN cookies fresh for the narration stream (#1566). Fire-and-forget:
    // never await ahead of play(), and ensureCdnCookies never throws.
    ensureCdnCookies();
    controller.play(rt.step, getAudioOpts());
    // Narrate once per step show: stepId/isShown are the show transition.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt && rt.stepId, rt && rt.isShown]);
  return null;
};

/** Bottom-right chip while the sandbox workspace is being created (R8). */
const GuidePreparingChip = ({ strings, zIndex }) => createPortal(
  <Paper
    role="status"
    elevation={0}
    data-testid="guide-preparing-chip"
    sx={(theme) => ({
      position: 'fixed',
      bottom: theme.ds.space[8],
      right: theme.ds.space[8],
      zIndex,
      display: 'flex',
      alignItems: 'center',
      gap: `${theme.ds.space[4]}px`,
      p: `${theme.ds.space[5]}px ${theme.ds.space[6]}px`,
      bgcolor: theme.palette.surface.overlay,
      color: theme.palette.text.secondary,
      fontSize: `${theme.ds.font.bar}px`,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: `${theme.ds.radius.container}px`,
      boxShadow: theme.palette.overlay.shadow,
      backgroundImage: 'none',
    })}
  >
    <CircularProgress size={16} color="inherit" />
    <Box component="span">{strings.preparing}</Box>
  </Paper>,
  document.body,
);

export const GuidesProvider = ({ children }) => {
  const theme = useTheme();
  const session = useUserSession();
  const group = useGroup();

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  const [active, setActive] = useState(null); // { entry, pkg }
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [hostError, setHostError] = useState(null); // LOAD_/SANDBOX_ERROR_REASON
  const [runId, setRunId] = useState(0);

  const userPrefs = (session && session.user && session.user.preferences) || {};
  const [voice, setVoiceState] = useState(getVoice());
  const [language, setLanguageState] = useState(
    LOCALE_CODE.test(userPrefs.language) ? userPrefs.language : DEFAULT_LOCALE,
  );
  const [languageConfig, setLanguageConfig] = useState({});

  // Refs mirror state so async callbacks read current values, not stale renders.
  const catalogRef = useRef([]);
  catalogRef.current = catalog;
  const activeRef = useRef(null);
  activeRef.current = active;
  const voiceRef = useRef(voice);
  voiceRef.current = voice;
  const localeRef = useRef(language);
  localeRef.current = language;
  // The real groupId before a sandbox swap, restored on exit (R8).
  const priorGroupRef = useRef(null);
  // The currently shown step, for the replay control.
  const currentStepRef = useRef(null);
  const autoStartedRef = useRef(false);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = createAudioController();

  const refreshCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const guides = await fetchCatalog();
      setCatalog(guides);
      setCatalogError(null);
    } catch (err) {
      setCatalogError(err.message);
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  // Silent background fetch of the catalog once the session is ready. Also
  // warms the CDN cookie session (#1566) so the first package/audio/KB read
  // is already credentialed; fire-and-forget, ensureCdnCookies never throws.
  useEffect(() => {
    if (session.loading || !session.user) return;
    ensureCdnCookies();
    refreshCatalog();
  }, [session.loading, session.user, refreshCatalog]);

  // Published locale config once. Non-fatal - on failure the Language menu
  // falls back to the active locale only.
  useEffect(() => {
    let cancelled = false;
    fetchLanguageConfig()
      .then((cfg) => { if (!cancelled) setLanguageConfig(cfg); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const exitSandbox = useCallback(() => {
    if (priorGroupRef.current) {
      group.setActiveGroupId(priorGroupRef.current);
      priorGroupRef.current = null;
    }
  }, [group]);

  // Sandbox guides (R8) run in the hidden practice-space workspace. The first
  // sandbox guide creates the group (idempotent); later guides reuse it.
  // Returns false when setup failed so start() can surface the error chip.
  const enterSandboxIfNeeded = useCallback(async (entry) => {
    if (!entry || !entry.sandbox) return true;
    let sandboxGroup = (group.groups || []).find((g) => g.guideSandbox === true);
    if (!sandboxGroup) {
      // Creating the group can take a second or two; show a labelled chip so
      // the wait does not read as a hang. A reuse is instant.
      setPhase(PHASE.PREPARING);
      try {
        sandboxGroup = await group.createSandboxGroup();
      } catch (err) {
        console.warn('guide sandbox setup failed:', err);
        return false;
      }
    }
    priorGroupRef.current = group.activeGroupId;
    group.setActiveGroupId(sandboxGroup.groupId);
    return true;
  }, [group]);

  // Tear the active run down. Restores the workspace; never writes progress.
  const teardownRun = useCallback(() => {
    audioRef.current.stop();
    currentStepRef.current = null;
    exitSandbox();
    setActive(null);
    setHostError(null);
    setPhase(PHASE.IDLE);
  }, [exitSandbox]);

  const startRef = useRef(() => {});
  /**
   * Launch a guide (launcher click or auto-start). Always a fresh run from
   * `firstStepId` (R7). A running guide is torn down first (no progress write).
   */
  const start = useCallback(async (moduleId) => {
    if (activeRef.current) teardownRun();
    const entry = catalogRef.current.find((g) => g.moduleId === moduleId);
    if (!entry) {
      setHostError(LOAD_ERROR_REASON);
      setPhase(PHASE.ERROR);
      return;
    }
    setPhase(PHASE.LOADING);
    setHostError(null);
    let pkg;
    try {
      pkg = await loadGuidePackage(entry.packageKey, localeRef.current, {
        moduleId: entry.moduleId,
        version: entry.version,
      });
    } catch (err) {
      console.warn('guide package load failed:', err);
      setHostError(LOAD_ERROR_REASON);
      setPhase(PHASE.ERROR);
      return;
    }
    const sandboxOk = await enterSandboxIfNeeded(entry);
    if (!sandboxOk) {
      setHostError(SANDBOX_ERROR_REASON);
      setPhase(PHASE.ERROR);
      return;
    }
    setActive({ entry, pkg });
    setRunId((n) => n + 1);
    setPhase(PHASE.RUNNING);
  }, [teardownRun, enterSandboxIfNeeded]);
  startRef.current = start;

  // `_exit` reached: the one progress write v3 makes (R7), then teardown. The
  // in-memory catalog is patched so the launcher badge flips without a refetch.
  const handleCompleted = useCallback((moduleId) => {
    const completedAt = new Date().toISOString();
    writeCompleted(moduleId, completedAt).catch((err) => {
      console.warn('guide progress write failed:', err);
    });
    setCatalog((prev) => prev.map((g) => {
      if (g.moduleId !== moduleId) return g;
      const progress = { ...(g.progress || {}), completed: completedAt };
      return { ...g, progress, effectivePriority: computeEffectivePriority(g.priority, progress) };
    }));
    teardownRun();
  }, [teardownRun]);

  // Exit (abandon): immediate, no confirm, no progress write (R7).
  const handleExit = useCallback(() => {
    teardownRun();
  }, [teardownRun]);

  const { pathname } = useLocation();

  // New-user auto-start (R7): every effective-`required` guide, catalog order;
  // one launch per mount, after a short orient delay. The route is an input
  // (#1808): nothing starts on the invitation page, and leaving it re-runs this
  // effect. The flag is set when the timer fires, so a route or catalog change
  // during the orient delay re-arms the launch instead of dropping it.
  useEffect(() => {
    if (autoStartedRef.current) return undefined;
    if (catalogLoading || catalogError || !session.user) return undefined;
    const auto = pickAutoStart(catalog, { user: session.user, pathname });
    if (auto.length === 0) return undefined;
    const timer = setTimeout(() => {
      autoStartedRef.current = true;
      startRef.current(auto[0].moduleId);
    }, AUTO_START_DELAY_MS);
    return () => clearTimeout(timer);
  }, [catalog, catalogLoading, catalogError, session.user, pathname]);

  const setVoiceGender = useCallback((gender) => {
    setVoice(gender);
    voiceRef.current = gender;
    setVoiceState(gender);
    // Muting also stops the stream already playing, not just the next step.
    if (gender === 'off') audioRef.current.stop();
  }, []);

  const replayAudio = useCallback(() => {
    const run = activeRef.current;
    const step = currentStepRef.current;
    if (!run || !step) return;
    audioRef.current.replay(step, {
      voiceGender: voiceRef.current,
      packageKey: run.entry.packageKey,
      locale: localeRef.current,
    });
  }, []);

  // Switch the guide locale. A running guide reloads in the new locale and
  // restarts from the top (its package is per-locale; no resume, R7). The
  // voice gender is clamped to one the new locale supports.
  const setLanguage = useCallback((lang) => {
    if (lang === localeRef.current) return;
    localeRef.current = lang;
    setLanguageState(lang);
    const genders = availableGenders(languageConfig, lang);
    if (voiceRef.current !== 'off' && !genders.includes(voiceRef.current)) {
      setVoiceGender(genders[0]);
    }
    const run = activeRef.current;
    if (run) startRef.current(run.entry.moduleId);
  }, [languageConfig, setVoiceGender]);

  const getAudioOpts = useCallback(() => ({
    voiceGender: voiceRef.current,
    packageKey: activeRef.current ? activeRef.current.entry.packageKey : '',
    locale: localeRef.current,
  }), []);

  // Engine chrome strings for the active locale (R9): locale -> en-US -> the
  // overlay's built-in defaults.
  const strings = useMemo(
    () => ({ ...DEFAULT_GUIDE_STRINGS, ...getEngineStrings(language) }),
    [language],
  );

  const availableVoiceGenders = availableGenders(languageConfig, language);

  const value = useMemo(() => ({
    // catalog
    catalog,
    catalogLoading,
    catalogError,
    refreshCatalog,
    // active run
    activeModuleId: active ? active.entry.moduleId : null,
    isRunning: phase === PHASE.RUNNING,
    // audio + locale
    voice,
    setVoiceGender,
    availableVoiceGenders,
    replayAudio,
    language,
    setLanguage,
    languageConfig,
    // actions
    start,
  }), [
    catalog, catalogLoading, catalogError, refreshCatalog,
    active, phase,
    voice, setVoiceGender, availableVoiceGenders, replayAudio,
    language, setLanguage, languageConfig,
    start,
  ]);

  const chipZ = theme.zIndex.modal + 102;

  return (
    <GuidesContext.Provider value={value}>
      {/* The app renders OUTSIDE the keyed runtime provider: nothing in it
          consumes the runtime context (only the bridge + overlay do), and the
          per-run key would otherwise unmount/remount the entire app on every
          guide start and teardown (#1042). */}
      {children}
      <GuideRuntimeProvider
        key={active ? `${active.entry.moduleId}:${runId}` : 'guide-idle'}
        guide={active ? active.pkg : null}
        autoStart
        onCompleted={handleCompleted}
        onExit={handleExit}
      >
        <GuideAudioBridge
          controller={audioRef.current}
          stepRef={currentStepRef}
          getAudioOpts={getAudioOpts}
        />
        <GuideOverlay strings={strings} onReplay={replayAudio} />
      </GuideRuntimeProvider>
      {phase === PHASE.PREPARING && (
        <GuidePreparingChip strings={strings} zIndex={chipZ} />
      )}
      {phase === PHASE.ERROR && createPortal(
        <GuideErrorChip
          reason={hostError}
          onExit={teardownRun}
          strings={strings}
          zIndex={chipZ}
        />,
        document.body,
      )}
    </GuidesContext.Provider>
  );
};

export const useGuides = () => {
  const ctx = useContext(GuidesContext);
  if (!ctx) throw new Error('useGuides must be used within GuidesProvider');
  return ctx;
};

/**
 * Non-throwing accessor: returns the guides context or null when no provider is
 * mounted. For consumers that only optionally use guide state (voice / locale),
 * e.g. the Knowledge Base narration (#1088), so they render fine in isolation.
 */
export const useGuidesOptional = () => useContext(GuidesContext);
